import { z } from 'zod';

export const activityLogUserSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  image: z.string().nullable(),
});

export type ActivityLogUserSchema = z.infer<typeof activityLogUserSchema>;

export const activityLogClientSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type ActivityLogClientSchema = z.infer<typeof activityLogClientSchema>;

export const activityLogPostSchema = z.object({
  id: z.string(),
  title: z.string(),
});

export type ActivityLogPostSchema = z.infer<typeof activityLogPostSchema>;

/**
 * Aggregated data from the entity the log's polymorphic modelId/modelType points at —
 * whatever a specific action/modelType combination needs to render its message,
 * so the client never has to look the entity up by id itself.
 */
export const activityLogSubjectSchema = z.object({
  title: z.string().nullable().optional(),
  fileName: z.string().nullable().optional(),
  commentContent: z.string().nullable().optional(),
  platformName: z.string().nullable().optional(),
  /** Resolved from payload.attributes.clientId when a task moved to a client. */
  clientName: z.string().nullable().optional(),
});

export type ActivityLogSubjectSchema = z.infer<typeof activityLogSubjectSchema>;

export const activityLogSchema = z.object({
  id: z.string(),
  action: z.string(),
  payload: z
    .object({
      attributes: z.record(z.string(), z.unknown()),
      previous: z.record(z.string(), z.unknown()),
    })
    .nullable(),
  modelId: z.string(),
  modelType: z.string(),
  userId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  /** The user who performed the action — includes badge props (image). */
  user: activityLogUserSchema.nullable(),
  /** Aggregated data from the modelId/modelType entity — see activityLogSubjectSchema. */
  subject: activityLogSubjectSchema.nullable(),
  /** Resolved from payload.attributes.userId when a task's assignee changed. */
  assignedUser: activityLogUserSchema.nullable(),
  /** The client this log is scoped to (from the log's own clientId). */
  client: activityLogClientSchema.nullable(),
  /** The post the log's target belongs to (itself, for modelType 'Post'). */
  post: activityLogPostSchema.nullable(),
});

export type ActivityLogSchema = z.infer<typeof activityLogSchema>;
